import {
  Box,
  createListCollection,
  HStack,
  Input,
  Portal,
  Select,
  Table,
  Text,
} from '@chakra-ui/react'
import { useCallback, useId, useMemo } from 'react'

import { ListToolbar, SortableColumnHeader } from '@mangovoice/mango-ui'
import type { TranslateFn } from '@mangovoice/mango-ui'

import type { DatePreset } from '../../constants/datePresets'
import { DATE_PRESET_LABELS } from '../../constants/datePresets'
import { WakeupCallOrigin } from '../../constants/wakeupStatuses'
import { useHotelsI18n } from '../../i18n'
import type { WakeupCall } from '../../types/wakeup'
import { valueOrPlaceholder } from '../../utils/display'
import {
  type ExtensionTimezoneMap,
  formatIsoInTimeZone,
  formatWakeupTime,
  getWakeupInstantIso,
  resolveWakeupDisplayTimeZone,
} from '../../utils/formatting'
import type { PageSize } from '../../utils/pagination'
import type {
  WakeupHistorySort,
  WakeupHistorySortField,
} from '../../utils/wakeupHistorySorting'
import { WakeupStatusBadge } from '../wakeup/WakeupStatusBadge'

const HISTORY_PAGE_SIZE_OPTIONS = [25, 50, 100, 250]

const TOOLBAR_FALLBACKS: Record<string, string> = {
  'hotels.pagination.resultsPerPage': 'Results per page:',
  'hotels.pagination.all': 'All',
  'hotels.pagination.previousPage': 'Previous page',
  'hotels.pagination.nextPage': 'Next page',
  'hotels.pagination.jumpToPage': 'Jump to page {{page}}',
}

interface WakeupHistoryTableProps {
  wakeupCalls: WakeupCall[]
  totalCount: number
  filteredCount: number
  startItem: number
  endItem: number
  currentPage: number
  pageSize: PageSize
  searchQuery: string
  datePreset: DatePreset
  onPageChange: (page: number) => void
  onPageSizeChange: (size: PageSize) => void
  onSearchChange: (query: string) => void
  onDatePresetChange: (preset: DatePreset) => void
  onSortChange: (
    field: WakeupHistorySortField,
    direction: WakeupHistorySort['direction'],
  ) => void
  pbxTimeZone: string
  extensionTzMap: ExtensionTimezoneMap
  sortState: WakeupHistorySort
}

const ShowingSummary = ({
  startItem,
  endItem,
  filteredCount,
  totalCount,
}: {
  startItem: number
  endItem: number
  filteredCount: number
  totalCount: number
}) => {
  const { text } = useHotelsI18n()
  const showing = text(
    'hotels.history.showing',
    'Showing {{start}} to {{end}} of {{count}} entries',
    {
      start: startItem,
      end: endItem,
      count: filteredCount,
    },
  )
  const filtered =
    filteredCount < totalCount
      ? ` ${text(
          'hotels.history.filteredFromTotal',
          '(filtered from {{total}} total entries)',
          { total: totalCount },
        )}`
      : ''

  return (
    <Text fontSize="xs" color="fg.muted">
      {showing}
      {filtered}
    </Text>
  )
}

export const WakeupHistoryTable = ({
  wakeupCalls,
  totalCount,
  filteredCount,
  startItem,
  endItem,
  currentPage,
  pageSize,
  searchQuery,
  datePreset,
  sortState,
  onPageChange,
  onPageSizeChange,
  onSearchChange,
  onDatePresetChange,
  onSortChange,
  pbxTimeZone,
  extensionTzMap,
}: WakeupHistoryTableProps) => {
  const { text, locale } = useHotelsI18n()
  const showLabelId = useId()
  const searchInputId = useId()
  const toolbarT: TranslateFn = useCallback(
    (key, opts) =>
      text(
        key,
        TOOLBAR_FALLBACKS[key] ?? key,
        opts as Record<string, string | number> | undefined,
      ),
    [text],
  )
  const datePresetCollection = useMemo(
    () =>
      createListCollection({
        items: (Object.keys(DATE_PRESET_LABELS) as DatePreset[]).map((key) => ({
          value: key,
          label: text(`hotels.datePresets.${key}`, DATE_PRESET_LABELS[key]),
        })),
      }),
    [text],
  )

  const headers: { field: WakeupHistorySortField; label: string }[] = [
    {
      field: 'room_number',
      label: text('hotels.history.room', 'Room'),
    },
    {
      field: 'room_name',
      label: text('hotels.history.roomName', 'Room Name'),
    },
    {
      field: 'attempt_count',
      label: text('hotels.history.attempts', 'Attempts'),
    },
    {
      field: 'wuc_timestamp',
      label: text('hotels.history.wakeupDateTime', 'Wake-up Date & Time'),
    },
    {
      field: 'activation_timestamp',
      label: text(
        'hotels.history.activationDateTime',
        'Activation Date & Time',
      ),
    },
    {
      field: 'display_status',
      label: text('hotels.history.wakeupStatus', 'Wake-up Status'),
    },
    {
      field: 'origination',
      label: text('hotels.history.originator', 'Originator'),
    },
  ]

  return (
    <Box
      borderWidth={1}
      borderColor="border.default"
      borderRadius="lg"
      bg="bg.default"
      overflow="hidden"
    >
      <HStack
        justify="space-between"
        align="center"
        px={4}
        py={2}
        bg="bg.subtle"
        borderBottomWidth={1}
        borderColor="border.default"
        flexWrap="wrap"
        gap={3}
      >
        <HStack gap={4} align="center" ml="auto">
          <HStack gap={2} align="center">
            <Text id={showLabelId} fontSize="sm" color="fg.subtle">
              {text('hotels.common.show', 'Show')}:
            </Text>
            <Select.Root
              ids={{ label: showLabelId }}
              collection={datePresetCollection}
              size="sm"
              width="150px"
              value={[datePreset]}
              onValueChange={(e) =>
                onDatePresetChange(e.value[0] as DatePreset)
              }
            >
              <Select.Trigger borderRadius="l2">
                <Select.ValueText />
              </Select.Trigger>
              <Portal>
                <Select.Positioner>
                  <Select.Content>
                    {datePresetCollection.items.map((opt) => (
                      <Select.Item key={opt.value} item={opt}>
                        {opt.label}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Portal>
            </Select.Root>
          </HStack>

          <HStack gap={2} align="center">
            <Text asChild fontSize="sm" color="fg.subtle">
              <label htmlFor={searchInputId}>
                {text('hotels.common.search', 'Search')}:
              </label>
            </Text>
            <Input
              id={searchInputId}
              size="sm"
              borderRadius="l2"
              w="180px"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              bg="bg.default"
              borderColor="border.default"
            />
          </HStack>
        </HStack>
      </HStack>

      <HStack
        justify="space-between"
        px={4}
        py={1}
        borderBottomWidth={1}
        borderColor="border.default"
      >
        <ShowingSummary
          startItem={startItem}
          endItem={endItem}
          filteredCount={filteredCount}
          totalCount={totalCount}
        />
      </HStack>

      <Box maxH="500px" overflowY="auto">
        <Table.Root size="sm" variant="line">
          <Table.Header>
            <Table.Row>
              {headers.map((header) => {
                const isActive = sortState.field === header.field
                return (
                  <SortableColumnHeader
                    key={header.field}
                    field={header.field}
                    label={header.label}
                    sortField={sortState.field}
                    direction={sortState.direction}
                    onSortChange={(field, direction) =>
                      onSortChange(field as WakeupHistorySortField, direction)
                    }
                    columnHeaderProps={{
                      position: 'sticky',
                      top: 0,
                      bg: 'bg.subtle',
                      zIndex: 1,
                      css: {
                        '& .sortable-column-header-icon': {
                          opacity: isActive ? 1 : 0,
                        },
                        '&:hover .sortable-column-header-icon': {
                          opacity: isActive ? 1 : 0.6,
                        },
                      },
                    }}
                  />
                )
              })}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {wakeupCalls.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={7} textAlign="center" py={8}>
                  <Text color="fg.muted">
                    {searchQuery
                      ? text(
                          'hotels.history.noMatchingRecords',
                          'No matching records found',
                        )
                      : text(
                          'hotels.history.noHistory',
                          'No wake-up call history',
                        )}
                  </Text>
                </Table.Cell>
              </Table.Row>
            ) : (
              wakeupCalls.map((wc) => (
                <Table.Row key={wc.id}>
                  <Table.Cell px={4} py={2}>
                    <Text fontSize="sm" fontWeight="medium">
                      {wc.room_number}
                    </Text>
                  </Table.Cell>
                  <Table.Cell px={4} py={2}>
                    <Text fontSize="sm">
                      {valueOrPlaceholder(wc.room_name, text)}
                    </Text>
                  </Table.Cell>
                  <Table.Cell px={4} py={2}>
                    <Text fontSize="sm">{wc.attempt_count}</Text>
                  </Table.Cell>
                  <Table.Cell px={4} py={2}>
                    <Text fontSize="sm">
                      {wc.wakeup_timestamp
                        ? formatWakeupTime(
                            wc.wakeup_timestamp,
                            locale,
                            resolveWakeupDisplayTimeZone(
                              wc,
                              extensionTzMap,
                              pbxTimeZone,
                            ),
                          )
                        : formatIsoInTimeZone(
                            getWakeupInstantIso(wc),
                            locale,
                            resolveWakeupDisplayTimeZone(
                              wc,
                              extensionTzMap,
                              pbxTimeZone,
                            ),
                          )}
                    </Text>
                  </Table.Cell>
                  <Table.Cell px={4} py={2}>
                    <Text fontSize="sm">
                      {formatIsoInTimeZone(wc.created_at, locale, pbxTimeZone)}
                    </Text>
                  </Table.Cell>
                  <Table.Cell px={4} py={2}>
                    <WakeupStatusBadge
                      displayStatus={wc.display_status ?? 'scheduled'}
                    />
                  </Table.Cell>
                  <Table.Cell px={4} py={2}>
                    <Text fontSize="sm">
                      {wc.origination === WakeupCallOrigin.PHONE
                        ? text('hotels.history.originPhone', 'Phone')
                        : wc.origination === WakeupCallOrigin.PORTAL
                          ? text('hotels.history.originPortal', 'Portal')
                          : text('hotels.common.unknown', 'Unknown')}
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </Box>

      <Box
        px={4}
        bg="bg.subtle"
        borderTopWidth={1}
        borderColor="border.default"
      >
        <ListToolbar
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          currentPage={currentPage}
          totalCount={filteredCount}
          onPageChange={onPageChange}
          isLoading={false}
          showToolbar
          pageSizeOptions={HISTORY_PAGE_SIZE_OPTIONS}
          showAllOption
          maxAllThreshold={Number.MAX_SAFE_INTEGER}
          size="sm"
          t={toolbarT}
          i18nPrefix="hotels.pagination"
        />
      </Box>
    </Box>
  )
}
